/*
 * <sf-vehicle-carousel> for sections/seatfit-vehicle-tabs-carousel.liquid.
 * The track is a native CSS scroll-snap list, so touch swipe and no-JS browsing already work.
 * This element only: keeps the active card + tab in sync with the scroll position, scrolls to the
 * first card of a tab on tab click, drives the desktop arrows, follows the theme editor, and carries the
 * landing page's ad params (utm_*, fbclid, gclid) and ?style= onto the card links.
 * The active card is computed from positions (not an IntersectionObserver) because peeking side
 * cards on desktop and the half-visible next card on mobile make visibility ratios ambiguous.
 */
(function () {
  'use strict';
  if (!window.customElements || customElements.get('sf-vehicle-carousel')) return;

  var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  // A tab/arrow click starts a smooth scroll that passes other cards; hold the chosen tab meanwhile.
  var LOCK_MS = 700;
  // Same params the picker's GO keeps (seatfit-vehicle-picker-lists.js KEEP_PARAM): ad attribution + the look.
  var KEEP_PARAM = /^(utm_[a-z0-9_]+|fbclid|gclid|style)$/i;
  var MAX_VALUE = 500; // an fbclid is ~100-200 chars; anything far longer is not ours to copy

  function toArray(list) { return Array.prototype.slice.call(list || []); }

  class SfVehicleCarousel extends HTMLElement {
    connectedCallback() {
      try {
        this.setup();
      } catch (e) {
        console.warn('[SeatFit] vehicle carousel init failed', e);
      }
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      clearTimeout(this.lockTimer);
    }

    setup() {
      this.track = this.querySelector('.sf-vtc__track');
      if (!this.track) return;
      this.cards = toArray(this.track.querySelectorAll('.sf-vtc__card'));
      this.tabs = toArray(this.querySelectorAll('.sf-vtc__tab'));
      this.prev = this.querySelector('.sf-vtc__arrow--prev');
      this.next = this.querySelector('.sf-vtc__arrow--next');
      this.active = -1;
      this.lockedUntil = 0;
      this.frame = 0;

      this.onScroll = this.onScroll.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      // Element-level listeners survive a disconnect/reconnect (editor re-order); add them once.
      if (!this.hasOwnListeners) {
        this.hasOwnListeners = true;
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('keydown', this.onKeydown.bind(this));
      }

      this.classList.add('is-ready');
      this.setActive(this.cards.length ? this.nearestIndex() : -1);
      this.keepAdParams();
    }

    // Ads land here with utm_* / fbclid (and maybe ?style=diamond, an ad aimed at one look). Liquid cannot read the
    // query string, so the card links that open a product directly pick them up here: attribution and the look survive
    // the jump as they do through the picker's GO. Cards that open the picker (href="#…") go through its goUrl.
    keepAdParams() {
      var keep = [];
      new URLSearchParams(location.search).forEach(function (v, k) {
        if (!KEEP_PARAM.test(k) || !v || v.length > MAX_VALUE) return;
        if (/^style$/i.test(k)) {
          if (!/^[a-z0-9-]{1,40}$/i.test(v)) return;
          v = v.toLowerCase();
        }
        keep.push([k, v]);
      });
      if (!keep.length) return;
      toArray(this.querySelectorAll('a.sf-vtc__link[href], a.sf-vtc__cta[href]')).forEach(function (a) {
        var href = a.getAttribute('href') || '';
        if (!href || href.charAt(0) === '#') return;
        try {
          var url = new URL(href, location.origin);
          if (url.origin !== location.origin) return;
          keep.forEach(function (p) { if (!url.searchParams.has(p[0])) url.searchParams.set(p[0], p[1]); });
          a.setAttribute('href', url.pathname + url.search + url.hash);
        } catch (e) { /* keep the card link as it is */ }
      });
    }

    isCentered() {
      var c = this.cards[0];
      return !!c && String(getComputedStyle(c).scrollSnapAlign).indexOf('center') !== -1;
    }

    padStart() {
      return parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
    }

    // Scroll position that snaps this card. offsetLeft is relative to the track (position: relative)
    // and ignores its scroll; clamped because the last cards cannot scroll past the end.
    snapLeft(card) {
      var t = this.track;
      var left = this.isCentered()
        ? card.offsetLeft - (t.clientWidth - card.offsetWidth) / 2
        : card.offsetLeft - this.padStart();
      return Math.max(0, Math.min(left, t.scrollWidth - t.clientWidth));
    }

    nearestIndex() {
      var left = this.track.scrollLeft;
      var best = 0;
      var bestDist = Infinity;
      for (var i = 0; i < this.cards.length; i++) {
        var d = Math.abs(this.snapLeft(this.cards[i]) - left);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      }
      return best;
    }

    setActive(index) {
      this.active = index;
      var tab = index >= 0 && this.cards[index] ? this.cards[index].dataset.tab : null;
      this.cards.forEach(function (c, i) { c.classList.toggle('is-active', i === index); });
      this.tabs.forEach(function (b) {
        var on = b.dataset.tab === tab;
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on || (!tab && b === this.tabs[0]) ? 0 : -1;
      }, this);
      if (this.prev) this.prev.disabled = index <= 0;
      if (this.next) this.next.disabled = index < 0 || index >= this.cards.length - 1;
    }

    scrollToIndex(index) {
      var card = this.cards[index];
      if (!card) return;
      var smooth = !(reduceMotion && reduceMotion.matches);
      this.lockedUntil = Date.now() + (smooth ? LOCK_MS : 0);
      this.track.scrollTo({ left: this.snapLeft(card), behavior: smooth ? 'smooth' : 'auto' });
      this.setActive(index);
      // Re-read once the smooth scroll settled, in case the user swiped during the lock.
      clearTimeout(this.lockTimer);
      this.lockTimer = setTimeout(this.sync.bind(this), LOCK_MS + 50);
    }

    goToTab(tab) {
      for (var i = 0; i < this.cards.length; i++) {
        if (this.cards[i].dataset.tab === tab) return this.scrollToIndex(i);
      }
    }

    sync() {
      if (Date.now() < this.lockedUntil || !this.cards.length) return;
      var i = this.nearestIndex();
      if (i !== this.active) this.setActive(i);
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(function () {
        this.frame = 0;
        this.sync();
      }.bind(this));
    }

    onClick(e) {
      var tab = e.target.closest('.sf-vtc__tab');
      if (tab && this.contains(tab)) return this.goToTab(tab.dataset.tab);
      var arrow = e.target.closest('.sf-vtc__arrow');
      if (arrow && this.contains(arrow) && !arrow.disabled) {
        var step = Number(arrow.dataset.dir) || 0;
        this.scrollToIndex(Math.max(0, Math.min(this.cards.length - 1, this.active + step)));
      }
    }

    // Arrow keys move between tabs (WAI-ARIA tabs pattern, automatic activation).
    onKeydown(e) {
      var tab = e.target.closest ? e.target.closest('.sf-vtc__tab') : null;
      if (!tab || (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft')) return;
      var i = this.tabs.indexOf(tab) + (e.key === 'ArrowRight' ? 1 : -1);
      var target = this.tabs[(i + this.tabs.length) % this.tabs.length];
      e.preventDefault();
      target.focus();
      this.goToTab(target.dataset.tab);
    }

    // Theme editor: selecting a vehicle block in the sidebar brings its card to the middle.
    onBlockSelect(e) {
      var card = e.target && e.target.closest ? e.target.closest('.sf-vtc__card') : null;
      if (card && this.contains(card)) this.scrollToIndex(this.cards.indexOf(card));
    }
  }

  customElements.define('sf-vehicle-carousel', SfVehicleCarousel);
})();
